import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocalShipping,
    Agriculture,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Add as AddIcon
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    IconButton,
    TextField,
    Button,
    Chip,
    Container,
    Paper,
    Stack,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert
} from '@mui/material';
import { customModal } from './CustomModal';

const MembershipManagement = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(null);
    const [formData, setFormData] = useState({
        price: '',
        offerPrice: '',
        GST: 18,
        description: '',
        vehicleType: 'Lorry',
        planName: '',
        validityDays: 30,
        monthlyOrderLimit: 10,
        includedOrders: 10,
        restrictions: '',
        isPopular: false,
        benefits: []
    });
    const [openAdd, setOpenAdd] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await axios.get('/admin/memberships');
            const data = response.data;
            setPlans(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching plans:', error);
            setLoading(false);
        }
    };

    const handleEdit = (plan) => {
        setEditMode(plan._id);
        setFormData({
            price: plan.price,
            offerPrice: plan.offerPrice,
            GST: plan.GST || 18,
            description: plan.description || '',
            validityDays: plan.validityDays || 30,
            monthlyOrderLimit: plan.monthlyOrderLimit !== undefined ? plan.monthlyOrderLimit : 10,
            includedOrders: plan.includedOrders !== undefined ? plan.includedOrders : 10,
            restrictions: plan.restrictions || ''
        });
    };

    const handleSave = async (id) => {
        try {
            const isNew = !id;
            const url = isNew ? '/admin/memberships' : `/admin/memberships/${id}`;
            const method = isNew ? 'post' : 'put';

            await axios({
                method: method,
                url: url,
                data: formData
            });

            setEditMode(null);
            setOpenAdd(false);
            fetchPlans();
            setToast({ open: true, message: `Plan ${isNew ? 'created' : 'updated'} successfully!`, severity: 'success' });
        } catch (error) {
            console.error('Error saving plan:', error);
            const msg = error.response?.data?.message || 'Network error';
            setToast({ open: true, message: `Failed to save plan: ${msg}`, severity: 'error' });
        }
    };

    const handleAddClick = () => {
        setFormData({
            price: '',
            offerPrice: '',
            GST: 18,
            description: '',
            vehicleType: 'Lorry',
            planName: '',
            validityDays: 30,
            monthlyOrderLimit: 10,
            includedOrders: 10,
            restrictions: '',
            isPopular: false,
            benefits: []
        });
        setOpenAdd(true);
    };

    const handleDeleteClick = async (id) => {
        const confirmed = await customModal.confirm("Delete Plan", "Are you sure you want to delete this membership plan?", { type: 'error', confirmText: 'Delete' });
        if (confirmed) {
            handleDelete(id);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/admin/memberships/${id}`);
            fetchPlans();
            setToast({ open: true, message: 'Plan deleted successfully!', severity: 'success' });
        } catch (error) {
            console.error('Error deleting plan:', error);
            setToast({ open: true, message: 'Failed to delete plan', severity: 'error' });
        }
    };

    const handleCancel = () => {
        setEditMode(null);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const renderPlanCard = (plan) => {
        const isEditing = editMode === plan._id;

        return (
            <Card
                key={plan._id}
                elevation={plan.isPopular ? 8 : 2}
                sx={{
                    position: 'relative',
                    mb: 4,
                    borderRadius: 4,
                    border: plan.isPopular ? '2px solid #ffb74d' : '1px solid #e0e0e0',
                    transition: 'transform 0.2s',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                    }
                }}
            >
                {/* {plan.isPopular && (
                    <Chip
                        icon={<Star style={{ color: 'white' }} />}
                        label="MOST POPULAR"
                        color="warning"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            fontWeight: 'bold',
                            color: 'white'
                        }}
                    />
                )} */}

                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography variant="overline" color="textSecondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                                {plan.vehicleType}
                            </Typography>
                            <Typography variant="h5" component="div" sx={{ fontWeight: 800, color: '#1a237e' }}>
                                {plan.planName}
                            </Typography>
                        </Box>
                        {!isEditing && (
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Edit Plan">
                                    <IconButton onClick={() => handleEdit(plan)} color="primary" size="small">
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Plan">
                                    <IconButton onClick={() => handleDeleteClick(plan._id)} color="error" size="small">
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}
                    </Box>

                    {isEditing ? (
                        <Stack spacing={3}>
                            <TextField
                                label="Original Price (₹)"
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                            />
                            <TextField
                                label="Offer Price (₹)"
                                type="number"
                                name="offerPrice"
                                value={formData.offerPrice}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                                helperText="Set to 0 if no offer"
                            />
                            <TextField
                                label="GST (%)"
                                type="number"
                                name="GST"
                                value={formData.GST}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>%</Typography> }}
                            />
                            <TextField
                                label="Validity (Days)"
                                type="number"
                                name="validityDays"
                                value={formData.validityDays}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>Days</Typography> }}
                            />
                            <TextField
                                label="Monthly Order Limit"
                                type="number"
                                name="monthlyOrderLimit"
                                value={formData.monthlyOrderLimit}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                helperText="Max orders driver can accept/receive per month"
                            />
                            <TextField
                                label="Orders Included"
                                type="number"
                                name="includedOrders"
                                value={formData.includedOrders}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Package Restrictions"
                                name="restrictions"
                                value={formData.restrictions}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                                placeholder="e.g. Max 10 orders/month, 15km radius limit"
                            />
                            <TextField
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                fullWidth
                                size="small"
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleSave(plan._id)}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                                    ₹{plan.offerPrice > 0 ? plan.offerPrice : plan.price}
                                </Typography>
                                <Typography variant="subtitle1" color="textSecondary" sx={{ ml: 1 }}>
                                    / month
                                </Typography>
                            </Box>

                            {(plan.offerPrice > 0 && plan.price > plan.offerPrice) && (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#9e9e9e' }}>
                                        ₹{plan.price}
                                    </Typography>
                                    <Chip
                                        label={`SAVE ₹${plan.price - plan.offerPrice}`}
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                </Stack>
                            )}

                            {/* Added GST, Validity, Order Limits, and Restrictions Display */}
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                                GST: {plan.GST || 18}% | Validity: {plan.validityDays || 30} Days
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Monthly Order Limit: {plan.monthlyOrderLimit !== undefined ? plan.monthlyOrderLimit : 10} orders/month | Included: {plan.includedOrders !== undefined ? plan.includedOrders : 10} orders
                            </Typography>
                            {plan.restrictions && (
                                <Typography variant="caption" display="block" color="error.main" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                                    Restrictions: {plan.restrictions}
                                </Typography>
                            )}

                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                                    {plan.description || "No description provided."}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const lorryPlans = plans.filter(p => p.vehicleType === 'Lorry');
    const tractorPlans = plans.filter(p => p.vehicleType === 'Tractor');

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#1a237e' }}>
                        Membership Plans
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Manage subscription packages and pricing for all vehicle types
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 'bold' }}
                >
                    Add New Plan
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={4}>
                    {/* Lorry Plans aColumn */}
                    <Grid item xs={12} lg={6}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#e3f2fd', borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                <LocalShipping sx={{ fontSize: 32, color: '#1976d2', mr: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                    Lorry Plans
                                </Typography>
                            </Box>
                            <Box>
                                {lorryPlans.length === 0 && <Typography color="textSecondary">No plans found.</Typography>}
                                {lorryPlans.map(plan => renderPlanCard(plan))}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Tractor Plans Column */}
                    <Grid item xs={12} lg={6}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                <Agriculture sx={{ fontSize: 32, color: '#2e7d32', mr: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                    Tractor Plans
                                </Typography>
                            </Box>
                            <Box>
                                {tractorPlans.length === 0 && <Typography color="textSecondary">No plans found.</Typography>}
                                {tractorPlans.map(plan => renderPlanCard(plan))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Add Plan Dialog */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>Add New Membership Plan</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="warning.main" sx={{ mb: 2, p: 1.5, bgcolor: '#fff8e1', borderRadius: 1 }}>
                        ⚠️ Default plans (Silver, Gold, Platinum) already exist for Lorry and Tractor. Use a different Plan Name to create a new plan.
                    </Typography>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Vehicle Type"
                                    select
                                    name="vehicleType"
                                    value={formData.vehicleType}
                                    onChange={handleChange}
                                    fullWidth
                                    SelectProps={{ native: true }}
                                >
                                    <option value="Lorry">Lorry</option>
                                    <option value="Tractor">Tractor</option>
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Plan Name"
                                    name="planName"
                                    placeholder="e.g. Silver, Gold, Platinum"
                                    value={formData.planName}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Original Price (₹)"
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleChange}
                                    fullWidth
                                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Offer Price (₹)"
                                    type="number"
                                    name="offerPrice"
                                    value={formData.offerPrice}
                                    onChange={handleChange}
                                    fullWidth
                                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="GST (%)"
                                    type="number"
                                    name="GST"
                                    value={formData.GST}
                                    onChange={handleChange}
                                    fullWidth
                                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>%</Typography> }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Validity (Days)"
                                    type="number"
                                    name="validityDays"
                                    value={formData.validityDays}
                                    onChange={handleChange}
                                    fullWidth
                                    InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>Days</Typography> }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Monthly Order Limit"
                                    type="number"
                                    name="monthlyOrderLimit"
                                    value={formData.monthlyOrderLimit}
                                    onChange={handleChange}
                                    fullWidth
                                    helperText="Max orders/month"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Orders Included"
                                    type="number"
                                    name="includedOrders"
                                    value={formData.includedOrders}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label="Package Restrictions"
                            name="restrictions"
                            value={formData.restrictions}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="e.g. Max 10 orders/month, 15km radius limit"
                        />
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenAdd(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSave()}
                        disabled={!formData.planName || !formData.price}
                    >
                        Create Plan
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToast({ ...toast, open: false })}
                    severity={toast.severity}
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default MembershipManagement;
